import { AppData, Colors, Lightning, Registry } from '@lightningjs/sdk';
import Tile, { TileTemplateSpec } from 'components/common/tiles/Tile';
import { STANDARD_EXPANSION, STANDARD_FADE } from 'support/animations';
import RatingBadge, { DEFAULT_SIZE } from 'components/common/RatingBadge';
import { translate } from 'support/translate';
import Button from 'components/common/buttons/Button';
import { Media, Show } from 'types/api/media';
import {
  formatPlayMedia,
  getDurationHourMinutesNoSpaceFormatted,
  isMovie,
  isShow,
} from 'support/contentUtils';
import { constants } from 'aliases';
import { getImageTextureObj } from 'support/generalUtils';
import { updateImageSize } from 'support/cwImageUtils';
import { VideoHistory } from 'services/history/HistoryService';
import ActionsList from '../ActionsList';
import TextLabel from '../TextLabel';

export interface FeaturedTileTemplateSpec extends TileTemplateSpec {
  expandedImageUrl: string;
  data: Show;

  ExpandedImage: {
    Gradient: Lightning.Component;
  };
  DetailsContent: {
    ImageLogo: Lightning.Component;
    InfoContainer: {
      RatingContainer: {
        RatingBadge: typeof RatingBadge;
        Info: typeof TextLabel;
      };
      Seasons: typeof TextLabel;
      Description: typeof TextLabel;
      ButtonContainer: typeof ActionsList;
    };
  };
}
const HIGHLIGHT_SIZE_OFFSET = constants.ui.highlightSizeOffset;
const FONT_SIZE = 23;

const DETAILS_X = 64;

const SEASONS_Y = 11;
const SEASONS_HEIGHT = 27;

const INFO_MARGIN = 24;
const INFO_CONTAINER_HEIGHT = 237 + INFO_MARGIN; // Include additional margin for text underhang cut off (g,j, etc)
const INFO_Y = 10;

const RATING_ROW_HEIGHT = 38;
const RATING_BADGE_MARGIN_LEFT = 2;
const RATING_BADGE_MARGIN_RIGHT = 8;

const DESCRIPTION_Y = 2;
const DESCRIPTION_WIDTH = 426;
const DESCRIPTION_TEXT_HEIGHT = 81 + DESCRIPTION_Y;

const BUTTONS_Y = 213;
const BUTTON_HEIGHT = 60;
const BUTTON_PLAY_WIDTH = 120;
const BUTTON_SPACING = 26;

const EXPANSION_DELAY = 1000;

// This value is non-zero so that the "invisible" component still renders (and doesn't have to render/fetch images when visible)
const INVISIBLE = constants.ui.invisible;

const PLAY_ICON_UNFOCUSED = getImageTextureObj(
  'static/images/playback/play-icon-light.svg',
  23,
  28,
);
const PLAY_ICON_FOCUSED = getImageTextureObj(
  'static/images/playback/play-icon-dark.svg',
  23,
  28,
);
const ARROW_RIGHT_ICON_UNFOCUSED = getImageTextureObj(
  'static/images/arrow-right-light.svg',
  35.3,
  30,
);
const ARROW_RIGHT_ICON_FOCUSED = getImageTextureObj(
  'static/images/arrow-right-dark.svg',
  35.3,
  30,
);

export default class FeaturedTile extends Tile<FeaturedTileTemplateSpec> {
  private _ExpandedImage = this.getByRef('ExpandedImage')!;
  private _DetailsContent = this.getByRef('DetailsContent')!;
  private _ImageLogo = this._DetailsContent.getByRef('ImageLogo')!;
  private _InfoContainer = this._DetailsContent.getByRef('InfoContainer')!;
  private _RatingContainer = this._InfoContainer.getByRef('RatingContainer')!;
  private _RatingBadge = this._RatingContainer.getByRef('RatingBadge')!;
  private _Info = this._RatingContainer.getByRef('Info')!;
  private _Seasons = this._InfoContainer.getByRef('Seasons')!;
  private _Description = this._InfoContainer.getByRef('Description')!;
  private _ButtonContainer = this._InfoContainer.getByRef('ButtonContainer')!;
  private _PlayButton = this._ButtonContainer.getItemByRef('PlayButton')!;
  private _GoToPageButton =
    this._ButtonContainer.getItemByRef('GoToPageButton')!;

  private expandTimeoutId: number | null = null;
  private imagesLoaded = 0;

  override get title() {
    if (!this._data) return '';

    return [
      this._data.title,
      this._data.rating,
      this._Info.text?.text ?? '',
      this._Seasons.visible ? this._Seasons.text?.text ?? '' : '',
      this._data.description,
    ];
  }

  set expandedImageUrl(expandedImageUrl: string) {
    // Only update image if we have an expanded image URL
    if (!expandedImageUrl) return;

    const src = updateImageSize(expandedImageUrl, this._ExpandedImage.w);
    this._ExpandedImage.patch({ src });
  }

  static override get expandedWidth() {
    return 0;
  }

  static override get expandedHeight() {
    return 0;
  }

  static get contentPaddingY() {
    return 0;
  }

  static get detailsOffsetY() {
    return 0;
  }

  static get logoWidth() {
    return 0;
  }

  static get logoHeight() {
    return 0;
  }

  static get logoMarginTop() {
    return 0;
  }

  static get buttonY() {
    return BUTTONS_Y;
  }

  get isExpanded() {
    const { expandedWidth, expandedHeight } = this
      .constructor as typeof FeaturedTile;

    return this.w === expandedWidth && this.h === expandedHeight;
  }

  static override _template(): Lightning.Component.Template<FeaturedTileTemplateSpec> {
    return {
      ...super._template(),
      transitions: STANDARD_EXPANSION,
      ExpandedImage: {
        transitions: STANDARD_FADE,
        alpha: INVISIBLE,
        rect: true,
        w: this.expandedWidth,
        h: this.expandedHeight,
        Gradient: {
          w: this.expandedWidth / 2,
          h: this.expandedHeight,
          colorLeft: Colors('background').get(),
          colorRight: Colors('transparent').get(),
          rect: true,
        },
      },
      DetailsContent: {
        alpha: INVISIBLE,
        transitions: {
          x: { duration: 0.1, timingFunction: 'ease-out' },
          ...STANDARD_FADE,
        },
        x: DETAILS_X,
        h: this.height,
        flex: { direction: 'column' },

        ImageLogo: {
          flexItem: { marginTop: this.logoMarginTop },
          w: this.logoWidth,
          h: this.logoHeight,
        },
        InfoContainer: {
          h: INFO_CONTAINER_HEIGHT,
          flex: {
            direction: 'column',
            justifyContent: 'center',
          },
          RatingContainer: {
            h: RATING_ROW_HEIGHT,
            flexItem: { marginTop: INFO_Y },
            flex: {
              direction: 'row',
              alignItems: 'center',
            },
            RatingBadge: {
              type: RatingBadge,
              flexItem: {
                marginLeft: RATING_BADGE_MARGIN_LEFT,
                marginRight: RATING_BADGE_MARGIN_RIGHT,
              },
              roundedRect: true,
            },
            Info: {
              type: TextLabel,
              text: {
                h: DEFAULT_SIZE.HEIGHT,
                offsetY: DEFAULT_SIZE.FONT_SIZE + DEFAULT_SIZE.PADDING_Y,
                fontSize: FONT_SIZE,
              },
            },
          },
          Seasons: {
            type: TextLabel,
            flex: { alignItems: 'center', alignContent: 'center' },
            flexItem: { marginTop: SEASONS_Y },
            text: {
              h: SEASONS_HEIGHT,
              textColor: Colors('tileSeasons').get(),
              fontSize: FONT_SIZE,
            },
          },
          Description: {
            type: TextLabel,
            flexItem: { marginTop: DESCRIPTION_Y },
            w: DESCRIPTION_WIDTH,
            h: DESCRIPTION_TEXT_HEIGHT,
            text: {
              fontSize: FONT_SIZE,
              maxLines: 3,
              maxLinesSuffix: '...',
              lineHeight: 27.45,
            },
          },
          ButtonContainer: {
            flexItem: { marginTop: INFO_MARGIN },
            flex: {
              direction: 'row',
            },
            type: ActionsList,
            spacing: BUTTON_SPACING,
            items: [
              {
                ref: 'PlayButton',
                type: Button,
                minWidth: BUTTON_PLAY_WIDTH,
                height: BUTTON_HEIGHT,
                fontSize: FONT_SIZE,
                padding: 0,
                action: '$play',
                startIcon: {
                  focused: PLAY_ICON_FOCUSED,
                  unfocused: PLAY_ICON_UNFOCUSED,
                },
                signals: {
                  $onHover: '$onHover',
                },
                passSignals: { $play: true },
              },
              {
                ref: 'GoToPageButton',
                type: Button,
                height: BUTTON_HEIGHT,
                fontSize: FONT_SIZE,
                padding: 0,
                action: '$goToPage',
                startIcon: {
                  focused: ARROW_RIGHT_ICON_FOCUSED,
                  unfocused: ARROW_RIGHT_ICON_UNFOCUSED,
                },
                signals: {
                  $onHover: '$onHover',
                },
                passSignals: { $goToPage: true },
              },
            ],
            signals: { $play: true, $goToPage: true },
          },
        },
      },
    };
  }

  override get isHoverable() {
    // Given complexities with cut off tiles and nested CTAs
    // Simplify logic to only allow hovering if the component is the primary/focused carousel
    return this.collectionWrapper.hasFocus() && super.isHoverable;
  }

  override _setup() {
    super._setup();

    const handleImageLoad = () => {
      this.imagesLoaded += 1;
      if (this.imagesLoaded === 2) {
        this._Placeholder.patch({ visible: false });

        this._hasImageLoaded = true;
        this.handleVisibilitySignal();
      }
    };
    const handleImageUnload = () => {
      this.imagesLoaded -= 1;
      this._Placeholder.patch({ visible: true });

      this._hasImageLoaded = false;
    };

    this._Image.on('txLoaded', handleImageLoad);
    this._ExpandedImage.on('txLoaded', handleImageLoad);
    this._Image.on('txUnloaded', handleImageUnload);
    this._ExpandedImage.on('txUnloaded', handleImageUnload);

    this._Image.patch({
      transitions: STANDARD_FADE,
    });
    this._Placeholder.patch({
      transitions: STANDARD_EXPANSION,
    });

    this._Placeholder.transition('w').on('finish', () => {
      this.handleDetailsTransition();
    });
    this._DetailsContent.transition('x').on('finish', () => {
      if (!this.hasFocus()) return;
    });

    this._HoverHighlight.patch({
      w: w => w + HIGHLIGHT_SIZE_OFFSET,
      h: h => h + HIGHLIGHT_SIZE_OFFSET,
    });

    this._setState('Collapsed');
  }

  override _active() {
    super._active();
    this._toggleGlobalHoverListener(true);
  }

  override _focus() {
    this.clearExpandTimeout();
    this.parseMediaItem();

    let dataPromise = AppData?.listItemPromiseService.get<
      VideoHistory | undefined
    >(this._data);

    /* 
    async call we let run independently
    if we allow this to run on active or when data is set, it could potentially make a lot of unnecessary calls to Persona
     */
    if (!dataPromise) {
      dataPromise = this.getContentHistory(this._data);
      AppData?.listItemPromiseService.add(this._data, dataPromise);
    }

    dataPromise.then(this.updateContentHistory.bind(this));

    this.startExpansionTimer();
  }

  override _unfocus() {
    // Since we have special isHoverable guards, we need to ensure that unfocusing
    // always implies loss of hover state to account for edge cases with hover + remote focus
    this.hovered = false;
    this.clearExpandTimeout();
    this.handleDetailsTransition();

    const { width, height } = this.constructor as typeof FeaturedTile;
    const tileSize = { w: width, h: height };

    this._Placeholder.patch({ smooth: tileSize });

    if (AppData?.device.getIsHovering()) {
      // TODO: Re-visit why if this is animated, scrolling expanded tiles has visual issues
      this.patch(tileSize);
    } else {
      this.patch({ smooth: tileSize });
    }
    this.updateImages();

    const highlightTransition = {
      smooth: {
        w: width + HIGHLIGHT_SIZE_OFFSET,
        h: height + HIGHLIGHT_SIZE_OFFSET,
      },
    };

    this.fireAncestors('$compressList', highlightTransition);

    this._setState('Collapsed');
  }

  protected override clearListeners() {
    super.clearListeners();
    this._ExpandedImage.removeAllListeners('txLoaded');
    this._ExpandedImage.removeAllListeners('txUnloaded');
    this._DetailsContent.transition('x').removeAllListeners('finish');
    this._Placeholder.transition('w').removeAllListeners('finish');
    this._toggleGlobalHoverListener(false);
  }

  private onRepositioned() {
    if (!this.hasFocus()) return;
    const { expandedWidth, expandedHeight } = this
      .constructor as typeof FeaturedTile;
    const tileSize = { w: expandedWidth, h: expandedHeight };

    this._Placeholder.patch({ smooth: tileSize });
    this.updateImages();

    const highlightTransition = {
      smooth: {
        w: expandedWidth + HIGHLIGHT_SIZE_OFFSET,
        h: expandedHeight + HIGHLIGHT_SIZE_OFFSET,
      },
    };

    this.fireAncestors('$expandList', highlightTransition);
  }

  private updateImages() {
    const hasFocus = this.hasFocus();

    const imageAlpha = hasFocus ? INVISIBLE : 1;
    this._Image.patch({ smooth: { alpha: imageAlpha } });

    const expandedImage = hasFocus ? 1 : INVISIBLE;
    this._ExpandedImage.patch({ smooth: { alpha: expandedImage } });
  }

  private handleDetailsTransition() {
    const hasFocus = this.hasFocus();
    const alpha = hasFocus ? 1 : 0;

    this._DetailsContent.patch({ smooth: { alpha } });
  }

  startExpansionTimer() {
    if (AppData!.device.getIsHovering()) return;

    this.clearExpandTimeout();
    this.expandTimeoutId = Registry.setTimeout(() => {
      this.handleExpansion();
    }, EXPANSION_DELAY);
  }

  handleExpansion() {
    if (this.isExpanded) return;

    const { expandedWidth, expandedHeight } = this
      .constructor as typeof FeaturedTile;
    const tileSize = { w: expandedWidth, h: expandedHeight };
    this.patch(tileSize);
    this.fireAncestors('$repositionList', this.onRepositioned.bind(this));
    this.expandTimeoutId = null;

    this._setState('Expanded');
  }

  private clearExpandTimeout() {
    if (this.expandTimeoutId !== null) {
      Registry.clearTimeout(this.expandTimeoutId);
      this.expandTimeoutId = null;
    }
  }

  private updateTotalSeasons(content: Show) {
    let info = '';
    if (isMovie(content)) {
      const releaseYear = content.releaseYear;
      const duration = getDurationHourMinutesNoSpaceFormatted(content);
      info =
        releaseYear && duration
          ? `${releaseYear} | ${duration}`
          : releaseYear || duration;
    } else {
      const totalSeasons = content.totalSeasons;
      info = totalSeasons
        ? translate(
            totalSeasons === 1 ? 'home.season' : 'home.seasons',
            totalSeasons,
          )
        : '';
    }
    if (info) {
      this._Seasons.patch({ visible: true, text: info });
    } else {
      this._Seasons.patch({ visible: false });
    }
  }

  private updateInfoContainer(genres: string[]) {
    const infoValue = genres.join(', ');
    this._Info.patch({
      text: { text: infoValue },
    });
  }

  private async getContentHistory(
    content: Show,
  ): Promise<VideoHistory | undefined> {
    const { guid, showSlug } = content.firstEpisode ?? {};
    if (!guid || !showSlug) return;

    return AppData?.historyService.getVideoHistory(showSlug, guid);
  }

  private async updateContentHistory(history: VideoHistory | undefined) {
    const playButtonLabel = formatPlayMedia(this._data, {
      progress: history?.progress,
      isComplete: history?.isComplete,
      isFromTitle: true,
    });

    this._PlayButton.patch({ label: playButtonLabel });
  }

  private parseMediaItem() {
    const imageLogoUrl = updateImageSize(
      this._data.images.image_logo,
      this._ImageLogo.w,
    );
    const rating = this._data.rating;
    const genres = this._data.genres;
    const description = this._data.description;

    this._ImageLogo.patch({ src: imageLogoUrl });
    this._RatingBadge.patch({ rating });
    this._Description.patch({ text: { text: description } });
    this._GoToPageButton.patch({ label: translate('featured.detail') });

    this.updateTotalSeasons(this._data);
    this.updateInfoContainer(genres);
  }

  $play() {
    let media: Media | undefined;
    if (this._data && isShow(this._data)) {
      media = (this._data as Show).firstEpisode ?? undefined;
    } else {
      media = this._data;
    }

    this.fireAncestors('$onTileSelected', media, {
      action: 'play',
      parentContext: this.parentContext,
      imageUrl: this._Image.src, // we use the non-expanded image
    });
  }

  $goToPage() {
    this.fireAncestors('$onTileSelected', this._data, {
      action: 'details',
      parentContext: this.parentContext,
    });
  }

  override onTileSelected() {
    if (AppData!.device.getIsHovering()) {
      this.handleHoverSelection();
      return;
    }

    this.$goToPage();
  }

  handleHoverSelection() {
    if (!this.isExpanded) {
      this.collectionWrapper.signal('onHoveringTileExpansion', this.itemIndex);
      this.handleExpansion();
    } else {
      if (this._ButtonContainer._getFocused().id === this._PlayButton.id) {
        this.$play();
      } else {
        this.$goToPage();
      }
    }
  }

  _onGlobalHoverStateChanged(isHovered: boolean) {
    isHovered ? this.onGlobalHoverEnabled() : this.onGlobalHoverDisabled();
  }

  _toggleGlobalHoverListener(enable: boolean) {
    this.baseToggleGlobalHoverListener(
      enable,
      this._onGlobalHoverStateChanged.bind(this),
      'FeaturedTile',
    );
  }

  private onGlobalHoverEnabled() {
    this.clearExpandTimeout();
  }

  private onGlobalHoverDisabled() {
    if (this.hasFocus() && this.collectionWrapper.index === this.itemIndex) {
      this.startExpansionTimer();
    }
  }

  static override _states() {
    return [
      class Collapsed extends this {
        override _getFocused() {
          return this;
        }
      },
      class Expanded extends this {
        override _getFocused() {
          return this._ButtonContainer;
        }
      },
    ];
  }
}
